import { z } from "zod/v4";
import { EmployeeName, LdapName, UidNumber } from "./primitives.ts";

/*
 * Request and response payloads of the portrait self-service's API.
 */

/**
 * Carries the caller's ID token. CloudFront signs its requests to the API
 * with its own `Authorization` header, which leaves no room for the token.
 */
export const ID_TOKEN_HEADER = "x-id-token";

/** The employee the request was authenticated as */
export const GetMeResponse = z.object({
  uidNumber: UidNumber,
  name: EmployeeName,
  ldapName: LdapName,
  /** Reviews everybody's portraits and changes them on their behalf */
  isAdmin: z.boolean(),
});
export type GetMeResponse = z.infer<typeof GetMeResponse>;

/** Formats the model backend can read */
export const PortraitUploadContentType = z.enum([
  "image/jpeg",
  "image/png",
  "image/webp",
]);
export type PortraitUploadContentType = z.infer<
  typeof PortraitUploadContentType
>;

export const MAX_PORTRAIT_UPLOAD_BYTES = 7 * 1024 * 1024;

export const CreatePortraitUploadRequest = z.object({
  contentType: PortraitUploadContentType,
});
export type CreatePortraitUploadRequest = z.infer<
  typeof CreatePortraitUploadRequest
>;

/** A presigned POST: send `fields` and then the file as form data to `url` */
export const CreatePortraitUploadResponse = z.object({
  uploadKey: z.string().nonempty(),
  url: z.url(),
  fields: z.record(z.string(), z.string()),
});
export type CreatePortraitUploadResponse = z.infer<
  typeof CreatePortraitUploadResponse
>;

/** Without an upload key, the cut-out of the current photo is redone */
export const RequestCutoutRequest = z.object({
  uploadKey: z.string().nonempty().optional(),
});
export type RequestCutoutRequest = z.infer<typeof RequestCutoutRequest>;

export const PortraitJobStatus = z.enum([
  "queued",
  "processing",
  "done",
  "failed",
]);
export type PortraitJobStatus = z.infer<typeof PortraitJobStatus>;

/** `broad` is the portrait editor's language-conditioned edit */
export const ChristmasImageMethod = z.enum(["focused", "broad"]);
export type ChristmasImageMethod = z.infer<typeof ChristmasImageMethod>;

export const ChristmasImageOptions = z.object({
  outfit: z.boolean(),
  method: ChristmasImageMethod,
});
export type ChristmasImageOptions = z.infer<typeof ChristmasImageOptions>;

export const RequestChristmasImageRequest = ChristmasImageOptions;
export type RequestChristmasImageRequest = ChristmasImageOptions;

/** `withChristmasImage` takes the latest finished Christmas image */
export const ChoosePortraitRequest = z.object({
  withChristmasImage: z.boolean(),
});
export type ChoosePortraitRequest = z.infer<typeof ChoosePortraitRequest>;

export const GetPortraitResponse = z.object({
  hasUpload: z.boolean(),
  cutout: z
    .object({
      status: PortraitJobStatus,
      /** Short-lived link to the latest finished cut-out */
      imageUrl: z.url().optional(),
    })
    .optional(),
  christmasImage: z
    .object({
      status: PortraitJobStatus,
      options: ChristmasImageOptions,
      /** Short-lived link to the latest finished Christmas image */
      imageUrl: z.url().optional(),
      chosen: z.boolean(),
    })
    .optional(),
  /** What the cards show; a new cut-out takes back a chosen Christmas image */
  choice: z
    .discriminatedUnion("kind", [
      z.object({ kind: z.literal("christmasImage"), imageUrl: z.url() }),
      z.object({ kind: z.literal("photoOnly") }),
    ])
    .optional(),
  /** An admin found what the cards would show unusable */
  flagged: z.boolean(),
});
export type GetPortraitResponse = z.infer<typeof GetPortraitResponse>;

export const FlagPortraitRequest = z.object({ flagged: z.boolean() });
export type FlagPortraitRequest = z.infer<typeof FlagPortraitRequest>;

export const PortraitSummary = GetPortraitResponse.pick({
  choice: true,
  flagged: true,
}).extend({
  cutoutUrl: z.url().optional(),
});
export type PortraitSummary = z.infer<typeof PortraitSummary>;

/** Every employee, including those who never opened the self-service */
export const ListPortraitsResponse = z.object({
  employees: z.array(
    z.object({
      uidNumber: UidNumber,
      name: EmployeeName,
      ldapName: LdapName.optional(),
      portrait: PortraitSummary.optional(),
    }),
  ),
});
export type ListPortraitsResponse = z.infer<typeof ListPortraitsResponse>;
